import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Reverb uses the Pusher protocol — expose Pusher globally as laravel-echo expects
(window as any).Pusher = Pusher;

let echo: Echo<'reverb'> | null = null;

/**
 * Initialise (or return the already-initialised) Echo singleton.
 * Returns null and logs a warning if the Reverb key was not built in — the
 * rest of the app must keep working (auth, API, AI chat, etc.) even when
 * real-time pub/sub is unavailable.
 */
export function initEcho(token: string): Echo<'reverb'> | null {
  if (echo) return echo;

  const key    = import.meta.env.VITE_REVERB_APP_KEY as string | undefined;
  const host   = import.meta.env.VITE_REVERB_HOST as string | undefined;
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

  if (!key || !host) {
    if (typeof console !== 'undefined') {
      console.warn('[echo] VITE_REVERB_APP_KEY or VITE_REVERB_HOST is missing — real-time features are disabled for this build.');
    }
    return null;
  }

  try {
    echo = new Echo({
      broadcaster: 'reverb',
      key,
      wsHost: host,
      wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8081),
      wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8081),
      forceTLS: import.meta.env.VITE_REVERB_SCHEME === 'https',
      enabledTransports: ['ws', 'wss'],
      // /broadcasting/auth is registered at the root by Laravel's withBroadcasting(),
      // NOT under /api. Strip any trailing /api segment from VITE_API_BASE_URL.
      authEndpoint: `${apiBase.replace(/\/api\/?$/, '')}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    });
  } catch (err) {
    console.warn('[echo] Failed to initialise real-time client', err);
    echo = null;
  }

  return echo;
}

/** Returns the current Echo instance, or null if not yet initialised. */
export function getEcho(): Echo<'reverb'> | null {
  return echo;
}

/** Disconnect and reset the singleton (call on logout). */
export function disconnectEcho(): void {
  echo?.disconnect();
  echo = null;
}
